export type MunichCityPassPassengerField = "firstName" | "lastName" | "birthDate";

export type MunichCityPassPassengerFieldErrors = Record<number, Partial<Record<MunichCityPassPassengerField, string>>>;

export type MunichCityPassApiFieldError = {
  field?: string;
  errorMessage: string;
};

export type MunichCityPassPassengerFieldErrorsResult = {
  byPassenger: MunichCityPassPassengerFieldErrors;
  other: MunichCityPassApiFieldError[];
};

// Backend echoes the estimation payload path with a 0-based index, e.g. "details.passengers[1].birthdate".
const PASSENGER_FIELD_PATH = /^(?:details\.)?passengers\[(\d+)\]\.(first_name|last_name|birthdate)$/;

const API_TO_PASSENGER_FIELD: Record<string, MunichCityPassPassengerField> = {
  first_name: "firstName",
  last_name: "lastName",
  birthdate: "birthDate"
};

/**
 * Maps a 400 estimation error body (`{ code, message, fields: [{ field, errorMessage }] }`) onto passenger rows.
 * Errors that can't be tied to an existing passenger input end up in `other`, so nothing is lost.
 */
export function getPassengerFieldErrors(
  prebookingError: any,
  passengerCount: number
): MunichCityPassPassengerFieldErrorsResult {
  const result: MunichCityPassPassengerFieldErrorsResult = { byPassenger: {}, other: [] };
  const fields = prebookingError?.fields;

  if (Number(prebookingError?.code) !== 400 || !Array.isArray(fields)) {
    return result;
  }

  fields.forEach((fieldError: any) => {
    const errorMessage = typeof fieldError?.errorMessage === "string" ? fieldError.errorMessage.trim() : "";
    if (!errorMessage) {
      return;
    }

    const path = typeof fieldError?.field === "string" ? fieldError.field.trim() : "";
    const match = PASSENGER_FIELD_PATH.exec(path);
    const index = match ? Number(match[1]) : -1;

    if (!match || index >= passengerCount) {
      result.other.push({ field: path || undefined, errorMessage });
      return;
    }

    const field = API_TO_PASSENGER_FIELD[match[2]];
    const passengerErrors = result.byPassenger[index] ?? {};
    const existingMessage = passengerErrors[field];

    passengerErrors[field] = existingMessage ? `${existingMessage} ${errorMessage}` : errorMessage;
    result.byPassenger[index] = passengerErrors;
  });

  return result;
}
